import { AddIcon, Button, ButtonGroup, DeleteIcon, EditIcon, Table, TableColumns, Tooltip } from '@bp/ui-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useLessonUsage } from '../../../hooks/useLessonUsage';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useClassDivisionsListQuery } from '../../../types/planung-graphql-client-defs';
import { UsedInLessonsTooltipContent } from '../../TooltipContents/LessonUsedInTooltipContent/UsedInLessonsTooltipContent';
import { ClassDivisionsClassType, ClassDivisionsListType } from '../graphql/types';
import styles from './_DivisionsTables.module.scss';

type DivisionsByClassTableProps = {
  classUuid: string;
  /** Titel „Teilungen <Klasse>“ ausblenden, z. B. wenn er schon im Modal-Titel steht */
  hideTitle?: boolean;
  readonly: boolean;
  onAdd: () => void;
  onDelete: (uuid: string) => void;
  onEdit: (uuid: string) => void;
};

export const DivisionsByClassTable = ({
  classUuid,
  hideTitle = false,
  readonly,
  onAdd,
  onDelete,
  onEdit,
}: DivisionsByClassTableProps) => {
  const { t } = useTranslation();
  const context = useMemorizedCacheTag('CLASS_DIVISIONS');

  const isMobile = useIsMobile();

  const [{ data }] = useClassDivisionsListQuery({
    variables: { classUuid },
    context,
  });

  const memoizedClass = useMemo((): Partial<ClassDivisionsClassType> => {
    return data?.class ?? {};
  }, [data]);

  const tableData = useMemo((): ClassDivisionsListType[] => {
    return data ? data.divisions : [];
  }, [data]);

  const check = useLessonUsage('classDivision', tableData);

  //biome-ignore lint/correctness/useExhaustiveDependencies(t): suppress dependency t
  const tableColumns: TableColumns<ClassDivisionsListType>[] = useMemo(() => {
    return [
      {
        header: t('groups.title', { count: 2 }),
        id: 'groupsConnections',
        accessorKey: 'groups',
        size: 400,
        canExpand: true,
        cell: ({ row }) => {
          const value = row.original.groups;
          return (
            <div className={styles['bars-wrapper']}>
              {value.map((node) => {
                return (
                  <Tooltip key={node.uuid} triggerClass={styles['bars-item']} content={node.name}>
                    <div className={styles.name} key={node.uuid}>
                      {node.shortName}
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          );
        },
      },
      {
        header: t('divisions.type'),
        accessorKey: 'name',
        id: 'name',
        size: 150,
        cell: ({ row }) => {
          return row.original.name === '0' ? '-' : row.original.name;
        },
      },
    ];
  }, []);

  // mobil: beschrifteter Button statt reinem „+“ der kompakten ActionBar
  const showMobileAdd = isMobile && !!classUuid;

  return (
    <>
      {showMobileAdd && (
        <Button
          className={styles['add-button']}
          disabled={readonly}
          hierarchy='secondary'
          icon={<AddIcon />}
          onClick={() => onAdd()}
        >
          {t('divisions.add')}
        </Button>
      )}
      <Table<ClassDivisionsListType>
        columns={tableColumns}
        data={tableData}
        isDesktop={!isMobile}
        customPadding={isMobile ? undefined : 'var(--spacing-6)'}
        hideHeader={tableData.length === 0}
        onAddClick={() => onAdd()}
        showActionBar={!!classUuid && !showMobileAdd}
        actionBarSettings={
          classUuid
            ? {
                showAddButton: true,
                disableAddButton: readonly,
                addButtonText: t('divisions.add'),
                showGlobalFilter: false,
                extendedActionsLeft: hideTitle ? undefined : (
                  <div className={styles.title}>{`${t('divisions.title', { count: 2 })} ${memoizedClass.name}`}</div>
                ),
              }
            : undefined
        }
        lastColWidth='80px'
        lastCol={(divisionRow) => {
          const used = check(divisionRow.original.uuid);
          const canBeDeleted = !readonly && !used.isUsed;
          const deleteButton = (
            <Button
              disabled={!canBeDeleted}
              hierarchy='tertiary'
              icon={<DeleteIcon className='small' color={canBeDeleted ? 'var(--color-error)' : undefined} />}
              onClick={() => onDelete(divisionRow.original.uuid)}
            />
          );
          return (
            <ButtonGroup disabled={readonly}>
              <Button
                disabled={readonly}
                hierarchy='secondary'
                type='button'
                icon={<EditIcon className='small' />}
                onClick={() => onEdit(divisionRow.original.uuid)}
              />
              {used.isUsed ? (
                <Tooltip
                  content={
                    <UsedInLessonsTooltipContent
                      lessons={used.lessons}
                      lessonsCount={used.lessonsCount}
                      usedInText={t('divisions.usedInLesson')}
                    />
                  }
                >
                  {deleteButton}
                </Tooltip>
              ) : (
                deleteButton
              )}
            </ButtonGroup>
          );
        }}
        className={styles['divisions-by-class-table']}
      />
    </>
  );
};
